// ============================================================================
// raidTargeting — pure helpers that resolve a raid target's subtree into the
// counts the HP formula and the arena renderers need. Kept off any React or
// Firestore surface so it stays trivially unit-testable.
//
// Post-book-model consumers should read `checklistItems` from the timeline
// context (already merged view) and pass it here — the deprecated
// `TimelineElement.totalChecklistCount` field is intentionally ignored.
// ============================================================================

import type { TimelineElement } from '../types/timelineElement'
import type { ChecklistItem } from '../types/schema'

export type RaidTargetKind = 'GOAL' | 'TASK' | 'SUBTASK'

export interface RaidTargetStats {
  /** Direct + indirect TASK/SUBTASK descendants. */
  nestedTaskCount: number
  /** Sum of `weight` across those descendants (≥ 1 minimum per node). */
  weightSum: number
  /** Per-task weight the HP formula wants (weightSum / max(1, count)). */
  weightMass: number
  /** Total checklist nodes attached anywhere in the subtree. */
  nestedChecklistCount: number
  /** Checklist nodes that have been completed already (for progress %). */
  completedChecklistCount: number
  /** Direct + indirect element descendants (informational, not HP input). */
  subtreeSize: number
  /** Elements whose boardStatus === 'COMPLETE' (informational). */
  subtreeCompletedCount: number
  /** Direct child elements in `elements` order — for arena renderers. */
  directChildren: TimelineElement[]
  /** Every subtree element flattened (excludes the target itself). */
  subtreeElements: TimelineElement[]
  /** Every checklist in the subtree, keyed by parent element id. */
  checklistBucket: Record<string, ChecklistItem[]>
}

const EMPTY_STATS: RaidTargetStats = {
  nestedTaskCount: 0,
  weightSum: 0,
  weightMass: 0,
  nestedChecklistCount: 0,
  completedChecklistCount: 0,
  subtreeSize: 0,
  subtreeCompletedCount: 0,
  directChildren: [],
  subtreeElements: [],
  checklistBucket: {},
}

/**
 * Walk the subtree rooted at `targetId`, collecting every TASK/SUBTASK
 * descendant and every checklist attached to any node in that subtree.
 */
export const computeRaidTargetStats = (
  targetId: string | null,
  elements: TimelineElement[],
  checklistItems: ChecklistItem[],
): RaidTargetStats => {
  if (!targetId) return EMPTY_STATS

  const byParent = new Map<string | null, TimelineElement[]>()
  elements.forEach((element) => {
    const bucket = byParent.get(element.parentId) ?? []
    bucket.push(element)
    byParent.set(element.parentId, bucket)
  })

  const target = elements.find((element) => element.id === targetId)
  if (!target) return EMPTY_STATS

  const subtree: TimelineElement[] = []
  const directChildren = byParent.get(targetId) ?? []
  const stack: TimelineElement[] = [...directChildren]
  while (stack.length > 0) {
    const node = stack.pop() as TimelineElement
    subtree.push(node)
    const children = byParent.get(node.id)
    if (children) stack.push(...children)
  }

  const subtreeIds = new Set<string>([targetId, ...subtree.map((node) => node.id)])
  const checklistBucket: Record<string, ChecklistItem[]> = {}
  let nestedChecklistCount = 0
  let completedChecklistCount = 0
  checklistItems.forEach((item) => {
    if (!subtreeIds.has(item.parentContainerId)) return
    if (item.board === 'ARCHIVE' || item.board === 'BIN') return
    nestedChecklistCount += 1
    if (item.isCompleted) completedChecklistCount += 1
    const bucket = checklistBucket[item.parentContainerId] ?? []
    bucket.push(item)
    checklistBucket[item.parentContainerId] = bucket
  })

  const taskLikeSubtree = subtree.filter((node) => node.type !== 'GOAL')
  const nestedTaskCount = taskLikeSubtree.length
  const weightSum = taskLikeSubtree.reduce((sum, node) => sum + Math.max(1, node.weight || 1), 0)
  const weightMass = weightSum / Math.max(1, nestedTaskCount)
  const subtreeCompletedCount = subtree.filter((node) => node.boardStatus === 'COMPLETE').length

  return {
    nestedTaskCount,
    weightSum,
    weightMass,
    nestedChecklistCount,
    completedChecklistCount,
    subtreeSize: subtree.length,
    subtreeCompletedCount,
    directChildren,
    subtreeElements: subtree,
    checklistBucket,
  }
}

/**
 * Format the HP formula summary shown in the forge preview and in the tooltip
 * for admins reviewing a Blueprint change.
 */
export const describeHpFormula = (stats: RaidTargetStats, hp: number): string =>
  `${stats.nestedTaskCount} tasks × ${stats.weightMass.toFixed(1)} wt × 10 + ${stats.nestedChecklistCount} checklists × 5 = ${hp} HP`

/** Ordered list of every candidate raid target in a workspace. */
export interface RaidTargetOption {
  id: string
  kind: RaidTargetKind
  title: string
  parentTitle: string | null
  progress: number
  boardStatus: TimelineElement['boardStatus']
}

export const buildRaidTargetOptions = (elements: TimelineElement[]): RaidTargetOption[] => {
  const byId = new Map(elements.map((element) => [element.id, element]))
  return elements
    .filter((element) => element.board !== 'ARCHIVE' && element.board !== 'BIN')
    .map((element) => ({
      id: element.id,
      kind: element.type,
      title: element.title || 'Untitled',
      parentTitle: element.parentId ? byId.get(element.parentId)?.title ?? null : null,
      progress: Math.max(0, Math.min(1, element.progress ?? 0)),
      boardStatus: element.boardStatus,
    }))
    .sort((a, b) => {
      // Goals first, then tasks, then subtasks; alphabetical inside each kind.
      const rank = (kind: RaidTargetKind) => (kind === 'GOAL' ? 0 : kind === 'TASK' ? 1 : 2)
      const kindDiff = rank(a.kind) - rank(b.kind)
      if (kindDiff !== 0) return kindDiff
      return a.title.localeCompare(b.title)
    })
}
